import React, { useState, useEffect } from 'react';
import apiClient from '../config/axiosConfig';
import moment from 'moment';
import {
  computeDayLayout,
  TRACK_INSET,
  BusyPeriod,
  BusinessHours,
  DaySelection,
  DayBlock
} from './dayViewLayout';
import './DayView.css';

interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
}

interface DayViewProps {
  bikeId: string;
  date: string;
  businessHours?: BusinessHours;
  selection?: DaySelection | null;
}

const DEFAULT_BUSINESS_HOURS: BusinessHours = { start: '10:00', end: '17:00' };

// Intl is used instead of a moment locale file so no extra locale bundle is needed.
const formatDutchDate = (date: string): string =>
  moment(date, 'YYYY-MM-DD').toDate().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

const DayView: React.FC<DayViewProps> = ({ bikeId, date, businessHours, selection }) => {
  const [busyPeriods, setBusyPeriods] = useState<BusyPeriod[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bikeId) {
      setBusyPeriods([]);
      return;
    }

    let cancelled = false;

    const fetchBusyPeriods = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get<ApiResponse<BusyPeriod[]>>(
          `/reservations/bike/${bikeId}/busy`
        );
        if (!cancelled) {
          setBusyPeriods(response.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching busy periods:', err);
        if (!cancelled) {
          setBusyPeriods([]);
          setError('De planning van deze dag kon niet worden geladen.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchBusyPeriods();

    return () => {
      cancelled = true;
    };
  }, [bikeId, date]);

  if (!bikeId || !date) {
    return null;
  }

  const layout = computeDayLayout(
    date,
    businessHours || DEFAULT_BUSINESS_HOURS,
    busyPeriods,
    selection
  );

  const renderBlock = (block: DayBlock, variant: 'busy' | 'selection'): JSX.Element => {
    const title = variant === 'busy' ? 'Bezet' : 'Jouw keuze';
    const time = `${block.startLabel} - ${block.endLabel}`;
    return (
      <div
        key={block.key}
        className={`day-view-block day-view-block-${variant}`}
        style={{ top: `${block.topPercent}%`, height: `${block.heightPercent}%` }}
        title={`${title} ${time}`}
      >
        <span className="day-view-block-title">{title}</span>
        <span className="day-view-block-time">{time}</span>
      </div>
    );
  };

  return (
    <div className="day-view">
      <div className="day-view-header">
        <h4>Dagoverzicht</h4>
        <span className="day-view-date">{formatDutchDate(date)}</span>
      </div>

      {isLoading ? (
        <div className="day-view-status">Planning wordt geladen...</div>
      ) : error ? (
        <div className="day-view-status day-view-status-error">{error}</div>
      ) : (
        <>
          <div
            className="day-view-track"
            style={
              {
                height: `${layout.trackHeight}px`,
                '--day-view-inset': `${TRACK_INSET}px`
              } as React.CSSProperties
            }
          >
            <div className="day-view-hours">
              {layout.hourMarkers.map(hour => (
                <div key={hour.key} className="day-view-hour" style={{ top: `${hour.topPercent}%` }}>
                  <span className="day-view-hour-label">{hour.label}</span>
                  <span className="day-view-hour-line" />
                </div>
              ))}
            </div>

            <div className="day-view-blocks">
              {layout.busyBlocks.map(block => renderBlock(block, 'busy'))}
              {layout.selectionBlock && renderBlock(layout.selectionBlock, 'selection')}
            </div>
          </div>

          {layout.busyBlocks.length === 0 && (
            <p className="day-view-empty">Deze dag is nog helemaal vrij.</p>
          )}

          <div className="day-view-legend">
            <span className="legend-item">
              <span className="legend-swatch legend-swatch-free" /> Vrij
            </span>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch-busy" /> Bezet
            </span>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch-selection" /> Jouw keuze
            </span>
          </div>

          <p className="day-view-privacy">
            Om privacyredenen tonen we alleen dat een tijd bezet is, niet door wie.
          </p>
        </>
      )}
    </div>
  );
};

export default DayView;
